import React, {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'

type DuckDbModule = typeof import('@duckdb/duckdb-wasm')
type DuckDbConstructor = DuckDbModule['AsyncDuckDB']
type DuckDbBundles = import('@duckdb/duckdb-wasm').DuckDBBundles
type DuckDbInstance = InstanceType<DuckDbConstructor>
type DuckDbInstanceContextValue = {
  duckDbInstance: DuckDbInstance | undefined
  ensureDuckDbInstance: () => Promise<DuckDbInstance>
}

async function createDuckDbInstance(): Promise<{
  db: DuckDbInstance
  worker: Worker
}> {
  const [
    duckdb,
    { default: ehWorker },
    { default: mvpWorker },
    { default: duckdbWasmEh },
    { default: duckdbWasm },
  ] = await Promise.all([
    import('@duckdb/duckdb-wasm'),
    import('@duckdb/duckdb-wasm/dist/duckdb-browser-eh.worker.js?url'),
    import('@duckdb/duckdb-wasm/dist/duckdb-browser-mvp.worker.js?url'),
    import('@duckdb/duckdb-wasm/dist/duckdb-eh.wasm?url'),
    import('@duckdb/duckdb-wasm/dist/duckdb-mvp.wasm?url'),
  ])

  const manualBundles: DuckDbBundles = {
    mvp: {
      mainModule: duckdbWasm,
      mainWorker: mvpWorker,
    },
    eh: {
      mainModule: duckdbWasmEh,
      mainWorker: ehWorker,
    },
  }

  const bundle = await duckdb.selectBundle(manualBundles)
  const worker = new Worker(bundle.mainWorker!)
  const logger = new duckdb.ConsoleLogger()
  const db = new duckdb.AsyncDuckDB(logger, worker)
  await db.instantiate(bundle.mainModule, bundle.pthreadWorker)

  return { db, worker }
}

/**
 * Returns an initialized instance of DuckDB-Wasm, or `undefined` if
 * initialization has not completed yet.
 */
export const DuckDbInstanceContext = createContext<DuckDbInstanceContextValue>({
  duckDbInstance: undefined,
  ensureDuckDbInstance: async () => {
    throw new Error('DuckDbInstanceProvider is not mounted')
  },
})

/**
 * Initializes a single instance of DuckDB-Wasm which individual components can
 * simply connect to. This is required to prevent out of memory errors that could
 * happen if too many instances of DuckDB are initialized at once.
 *
 * Note: DuckDB is an in-process analytical database which we are using to read data
 * out of parquet files. It should NOT be confused with Rolodex, which is still our
 * overall data store and single source of truth for data.
 */
export const DuckDbInstanceProvider = ({
  children,
}: {
  children: React.ReactNode
}) => {
  const [duckDbInstance, setDuckDbInstance] = useState<DuckDbInstance>()
  const initializationPromiseRef = useRef<Promise<DuckDbInstance> | null>(null)
  const workerRef = useRef<Worker | null>(null)
  const isMountedRef = useRef(true)

  useEffect(() => {
    return () => {
      isMountedRef.current = false
      workerRef.current?.terminate()
      workerRef.current = null
    }
  }, [])

  const ensureDuckDbInstance = useCallback(async () => {
    if (duckDbInstance) return duckDbInstance

    if (!initializationPromiseRef.current) {
      initializationPromiseRef.current = createDuckDbInstance()
        .then(({ db, worker }) => {
          workerRef.current = worker
          if (isMountedRef.current) {
            setDuckDbInstance(db)
          }
          return db
        })
        .catch((error) => {
          initializationPromiseRef.current = null
          workerRef.current?.terminate()
          workerRef.current = null
          throw error
        })
    }

    return initializationPromiseRef.current
  }, [duckDbInstance])

  const contextValue = useMemo(
    () => ({
      duckDbInstance,
      ensureDuckDbInstance,
    }),
    [duckDbInstance, ensureDuckDbInstance]
  )

  return (
    <DuckDbInstanceContext.Provider value={contextValue}>
      {children}
    </DuckDbInstanceContext.Provider>
  )
}
